import { useEffect, useState } from 'react'

import { getOrganisationByReferenceId } from 'api/api'
import { useCenterStore } from '@caradasstore/CenterStore'
import { useOrganisationStore } from '@caradasstore/OrganisationStore'
import { Logger } from 'logger'

export const SCHEDULER_ORGANISATION_STATUS = {
  LOADING: 'loading',
  READY: 'ready',
  MISSING: 'missing',
  ERROR: 'error'
}

const belongsToCenter = (organisation, ccId) => Boolean(organisation?.id) && String(organisation?.reference_id) === String(ccId)

/**
 * The current center's scheduler organisation, loaded on demand.
 *
 * The organisation store is only filled by the Calendar and Scheduler pages, and is reset
 * to id 0 on every login. Anything that books an appointment from elsewhere (the quote
 * pages) would otherwise call the scheduler with organisation 0 and get "Scheduler Account
 * not found". A stored organisation is reused only when it belongs to the signed-in center.
 */
export const useSchedulerOrganisation = () => {
  const ccId = useCenterStore((store) => store.ccId)
  const organisationDetail = useOrganisationStore((store) => store.organisationDetail)
  const setOrganisationDetail = useOrganisationStore((store) => store.setOrganisationDetail)
  const isStoredForCenter = belongsToCenter(organisationDetail, ccId)
  const [status, setStatus] = useState(isStoredForCenter ? SCHEDULER_ORGANISATION_STATUS.READY : SCHEDULER_ORGANISATION_STATUS.LOADING)

  useEffect(() => {
    if (isStoredForCenter) {
      setStatus(SCHEDULER_ORGANISATION_STATUS.READY)
      return undefined
    }
    if (!ccId) return undefined
    let cancelled = false
    setStatus(SCHEDULER_ORGANISATION_STATUS.LOADING)
    const fetchOrganisation = async () => {
      try {
        const response = await getOrganisationByReferenceId({ ccId })
        if (cancelled) return
        if (response?.data?.id) {
          setOrganisationDetail(response.data)
          setStatus(SCHEDULER_ORGANISATION_STATUS.READY)
        } else {
          setStatus(SCHEDULER_ORGANISATION_STATUS.MISSING)
        }
      } catch (error) {
        if (cancelled) return
        Logger.error({ message: error, payload: { ccId, path: 'useSchedulerOrganisation' } })
        setStatus(error?.response?.status === 404 ? SCHEDULER_ORGANISATION_STATUS.MISSING : SCHEDULER_ORGANISATION_STATUS.ERROR)
      }
    }
    fetchOrganisation()
    return () => {
      cancelled = true
    }
  }, [ccId, isStoredForCenter, setOrganisationDetail])

  return { status, organisation: isStoredForCenter ? organisationDetail : null }
}
